import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { X, CheckCircle2, Clock, Check, AlertCircle, Plus } from 'lucide-react';
import { BakeryEvent } from '../types/event';
import { INFO_EMAIL, sendBokabordMail, sendInfoMail } from '../utils/infoMail';
import { createBookingNumber, saveEventBooking } from '../utils/eventSheet';
import { formatPhone, validateEmail, validateName, validatePhone } from '../utils/validation';
interface EventSignupModalProps {
  event: BakeryEvent;
  onClose: () => void;
}
type Field = 'firstName' | 'lastName' | 'email' | 'phone';
interface ChildEntry {
  id: number;
  first: string;
  last: string;
  touchedFirst: boolean;
  touchedLast: boolean;
}
const MAX_CHILDREN = 8;
const ease = [0.23, 1, 0.32, 1] as const;
export function EventSignupModal({
  event,
  onClose
}: EventSignupModalProps) {
  const isChild = !!event.childSignup;
  const nextChildId = useRef(1);
  const [children, setChildren] = useState<ChildEntry[]>([{
    id: 0,
    first: '',
    last: '',
    touchedFirst: false,
    touchedLast: false
  }]);
  const updateChild = (id: number, patch: Partial<ChildEntry>) => setChildren(list => list.map(c => c.id === id ? {
    ...c,
    ...patch
  } : c));
  const addChild = () => {
    const id = nextChildId.current++;
    setChildren(list => [...list, {
      id,
      first: '',
      last: '',
      touchedFirst: false,
      touchedLast: false
    }]);
    requestAnimationFrame(() => document.getElementById(`su-child-${id}-first`)?.focus());
  };
  const removeChild = (id: number) => setChildren(list => list.filter(c => c.id !== id));
  const childErrors = children.map(c => ({
    first: validateName(c.first, 'förnamn'),
    last: validateName(c.last, 'efternamn')
  }));
  const childrenValid = !isChild || childErrors.every(e => !e.first && !e.last);
  const [values, setValues] = useState<Record<Field, string>>({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [touched, setTouched] = useState<Record<Field, boolean>>({
    firstName: false,
    lastName: false,
    email: false,
    phone: false
  });
  const [choice, setChoice] = useState<string | null>(null);
  const [choiceTouched, setChoiceTouched] = useState(false);
  const choiceError = event.bookingOptions && !choice ? 'Välj ett alternativ.' : null;
  const [party, setParty] = useState('');
  const [partyTouched, setPartyTouched] = useState(false);
  const selectedChoice = event.bookingOptions?.choices.find(c => c.label === choice);
  const partyCfg = selectedChoice?.partySize ?? event.partySize;
  const partyNum = Number(party);
  const partyError = !partyCfg ? null : party.trim() === '' ? 'Ange hur många ni är.' : !Number.isInteger(partyNum) ? 'Ange ett helt antal personer.' : partyNum < partyCfg.min ? partyCfg.minMessage ?? `Ni behöver vara minst ${partyCfg.min} personer för att boka.` : partyNum > partyCfg.max ? `Max ${partyCfg.max} personer per bokning – hör av dig till oss för större sällskap.` : null;
  const stepParty = (delta: number) => {
    setPartyTouched(true);
    setParty(p => {
      if (!partyCfg) return p;
      const current = p.trim() === '' || isNaN(Number(p)) ? partyCfg.min - delta : Number(p);
      return String(Math.min(partyCfg.max, Math.max(partyCfg.min, current + delta)));
    });
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const errors: Record<Field, string | null> = {
    firstName: validateName(values.firstName, 'förnamn'),
    lastName: validateName(values.lastName, 'efternamn'),
    email: validateEmail(values.email),
    phone: validatePhone(values.phone)
  };
  const isValid = Object.values(errors).every(e => e === null);
  const dateLabel = new Date(`${event.date}T12:00:00`).toLocaleDateString('sv-SE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });
  const firstName = values.firstName.trim();
  const lastName = values.lastName.trim();
  const fullName = `${firstName} ${lastName}`;
  const childList = children.map(c => ({
    first: c.first.trim(),
    last: c.last.trim()
  }));
  const childNames = childList.map(c => `${c.first} ${c.last}`);
  const childFirstNames = childList.map(c => c.first);
  const childFirstNamesText = childFirstNames.length > 1 ? `${childFirstNames.slice(0, -1).join(', ')} och ${childFirstNames[childFirstNames.length - 1]}` : childFirstNames[0];
  const setField = (field: Field) => (e: React.ChangeEvent<HTMLInputElement>) => setValues(v => ({
    ...v,
    [field]: e.target.value
  }));
  const touch = (field: Field) => () => setTouched(t => ({
    ...t,
    [field]: true
  }));
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setChildren(list => list.map(c => ({
      ...c,
      touchedFirst: true,
      touchedLast: true
    })));
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      phone: true
    });
    setChoiceTouched(true);
    setPartyTouched(true);
    if (choiceError) {
      document.getElementById('su-choice-0')?.focus();
      if (isValid) return;
    }
    if (partyError) {
      document.getElementById('su-party')?.focus();
      if (isValid) return;
    }
    if (!childrenValid) {
      const i = childErrors.findIndex(e => e.first || e.last);
      const c = children[i];
      document.getElementById(`su-child-${c.id}-${childErrors[i].first ? 'first' : 'last'}`)?.focus();
      return;
    }
    if (!isValid) {
      const firstInvalid = (Object.keys(errors) as Field[]).find(f => errors[f]);
      if (firstInvalid) document.getElementById(`su-${firstInvalid}`)?.focus();
      return;
    }
    setIsSubmitting(true);
    const email = values.email.trim();
    const phone = formatPhone(values.phone);
    const timeText = selectedChoice?.time ?? `${event.time ?? 'Ej angiven'}${event.secondaryTime ? ` (${event.secondaryTime})` : ''}`;
    const bookingNumber = createBookingNumber(event.id);
    const choiceLabel = ({
      'halloween-run-club': 'DELTAR I:  ',
      'halloween-hidden-reality': 'SPELFORM:  ',
      'halloween-hostbrunch': 'SITTNING:  '
    } as Record<string, string>)[event.id] ?? 'VAL:       ';
    const message = [`════════════════════════════════`, `   NY BOKNING – HALLOWEEK`, `   Gäller: ${event.title}`, `   ${bookingNumber}`, `════════════════════════════════`, '', `▸ DEL:        Halloweek (vecka 44)`, `▸ EVENT:      ${event.title}`, `▸ DATUM:      ${event.date} (${dateLabel})`, `▸ TID:        ${timeText}`, ...(choice ? [`▸ ${choiceLabel} ${choice.toUpperCase()}`] : []), ...(partyCfg ? [`▸ SÄLLSKAP:   ${partyNum} personer`] : []), '', ...(isChild ? [`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, `   BARN (${childNames.length} st)`, `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, ...childNames.map((n, i) => `  ${i + 1}. ${n}`), ''] : []), `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, isChild ? `   FÖRÄLDER / KONTAKTPERSON` : `   DELTAGARE`, `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, `  Namn:      ${fullName}`, `  E-post:    ${email}`, `  Telefon:   ${phone}`, '', `════════════════════════════════`, isChild ? `Svara direkt på detta mejl för att nå föräldern.` : `Svara direkt på detta mejl för att nå deltagaren.`, `════════════════════════════════`].join('\n');

    // Sheet and email run independently – one failing never blocks the other
    const mail = {
      subject: `[HALLOWEEK · ${event.title}] ${event.date}${choice ? ` · ${choice}` : ''}${partyCfg ? ` – ${partyNum} pers` : ''} – ${isChild ? `${childNames.length} barn: ${childNames.join(', ')} (förälder: ${fullName})` : fullName} (${bookingNumber})`,
      message,
      fromName: `Top Notch Halloweek – ${fullName}`,
      replyTo: email
    };
    const sheetBase = {
      eventId: event.id,
      bookingNumber,
      date: event.date,
      time: timeText,
      choice,
      partySize: partyCfg ? partyNum : null,
      firstName,
      lastName,
      email,
      phone
    };
    // Child events: one sheet row per child, all sharing the booking number
    const saveSheet = isChild ? Promise.all(childList.map(c => saveEventBooking({
      ...sheetBase,
      childFirstName: c.first,
      childLastName: c.last
    }))).then(results => results.every(Boolean)) : saveEventBooking(sheetBase);
    const [sheetOk, mailOk, bokabordOk] = await Promise.all([saveSheet, sendInfoMail(mail), sendBokabordMail(mail)]);
    if (!sheetOk) console.error('SheetDB: bokningen kunde inte sparas');
    if (!mailOk) console.error('Web3Forms: mejlet till info@ kunde inte skickas');
    if (!bokabordOk) console.error('Web3Forms: mejlet till bokabord@ kunde inte skickas');
    const ok = sheetOk || mailOk || bokabordOk;
    setIsSubmitting(false);
    if (ok) {
      setSent(true);
    } else {
      setError(`Något gick fel. Försök igen eller mejla oss på ${INFO_EMAIL}.`);
    }
  };
  const renderField = (field: Field, label: string, props: React.InputHTMLAttributes<HTMLInputElement>) => {
    const showError = touched[field] && errors[field];
    const showValid = touched[field] && !errors[field];
    return <div data-id="element-488" data-mp="3qm2x8yro51u9">
        <label htmlFor={`su-${field}`} className="block text-xs font-semibold text-[#F5EDE3]/70 mb-1.5" data-id="element-489" data-mp="3qnnsyrppyqtp">
          {label}
        </label>
        <div className="relative" data-id="element-490" data-mp="xewdluh3wicx">
          <input id={`su-${field}`} value={values[field]} onChange={setField(field)} onBlur={touch(field)} aria-invalid={!!showError} aria-describedby={showError ? `su-${field}-error` : undefined} className={`w-full h-11 pl-3 pr-9 rounded-lg bg-[#0E0C0B] text-[#F5EDE3] placeholder:text-[#F5EDE3]/30 text-sm outline-none border transition-[border-color,box-shadow] duration-150 focus:ring-2 ${showError ? 'border-[#F87171] focus:ring-[#F87171]/25' : 'border-[#F5EDE3]/15 focus:border-[#FF7A1A] focus:ring-[#FF7A1A]/25'}`} {...props} data-id="element-491" data-mp="xdl9tunnvalt" />
          {showValid && <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FF9A4D]" aria-hidden="true" data-id="element-492" data-mp="1zug2j2b23vc8" />}
          {showError && <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F87171]" aria-hidden="true" data-id="element-493" data-mp="2ccij6689aot0" />}
        </div>
        {showError && <p id={`su-${field}-error`} className="mt-1.5 text-xs font-medium text-[#FCA5A5]" data-id="element-494" data-mp="22e2sg4d55hk1">
            {errors[field]}
          </p>}
      </div>;
  };
  const renderChildField = (c: ChildEntry, errs: {
    first: string | null;
    last: string | null;
  }, key: 'first' | 'last', label: string) => {
    const id = `su-child-${c.id}-${key}`;
    const t = key === 'first' ? c.touchedFirst : c.touchedLast;
    const err = errs[key];
    const showError = t && err;
    const showValid = t && !err;
    return <div data-id="element-495" data-mp="3917yppwev5bt">
        <label htmlFor={id} className="block text-xs font-semibold text-[#F5EDE3]/70 mb-1.5" data-id="element-496" data-mp="3dthl4vybhn9c">
          {label}
        </label>
        <div className="relative" data-id="element-497" data-mp="3np83jvzraybf">
          <input id={id} type="text" autoComplete="off" maxLength={40} value={c[key]} onChange={e => updateChild(c.id, {
          [key]: e.target.value
        })} onBlur={() => updateChild(c.id, key === 'first' ? {
          touchedFirst: true
        } : {
          touchedLast: true
        })} aria-invalid={!!showError} aria-describedby={showError ? `${id}-error` : undefined} className={`w-full h-11 pl-3 pr-9 rounded-lg bg-[#0E0C0B] text-[#F5EDE3] placeholder:text-[#F5EDE3]/30 text-sm outline-none border transition-[border-color,box-shadow] duration-150 focus:ring-2 ${showError ? 'border-[#F87171] focus:ring-[#F87171]/25' : 'border-[#F5EDE3]/15 focus:border-[#FF7A1A] focus:ring-[#FF7A1A]/25'}`} data-id="element-498" data-mp="3iqnacgix9ewh" />
          {showValid && <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FF9A4D]" aria-hidden="true" data-id="element-499" data-mp="wih789691wmm" />}
          {showError && <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F87171]" aria-hidden="true" data-id="element-500" data-mp="16iriho8q4tz5" />}
        </div>
        {showError && <p id={`${id}-error`} className="mt-1.5 text-xs font-medium text-[#FCA5A5]" data-id="element-501" data-mp="dulxr1ho74zb">
            {err}
          </p>}
      </div>;
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.2,
    ease
  }} className="halloween-theme fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-id="element-502" data-mp="mrwayse1t46v">
      <motion.div role="dialog" aria-modal="true" aria-labelledby="event-signup-title" initial={{
      opacity: 0,
      scale: 0.96,
      y: 8
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} transition={{
      duration: 0.25,
      ease
    }} onClick={e => e.stopPropagation()} className="bg-[#1C1917] text-[#F5EDE3] rounded-2xl shadow-2xl w-full max-w-md overflow-hidden my-8 border-t-4 border-[#FF7A1A]" data-id="element-503" data-mp="3v7pmstxfprhl">
        <div className="px-6 pt-6 pb-5 border-b border-[#F5EDE3]/10 flex items-start justify-between gap-4" data-id="element-504" data-mp="3uuvjq1bxyhin">
          <div className="min-w-0" data-id="element-505" data-mp="3pwkbtw57ih32">
            <p className="text-xs font-bold text-[#FF9A4D] mb-1.5" data-id="element-506" data-mp="3pviuj9xlkycw">
              Boka in dig · Halloweek
            </p>
            <h2 id="event-signup-title" className="text-2xl font-bold leading-tight" data-id="element-507" data-mp="8luimery5ate">
              {event.title}
            </h2>
            <p className="mt-2 text-sm text-[#F5EDE3]/60 flex items-center gap-1.5" data-id="element-508" data-mp="2aftvh4kpw087">
              <Clock className="w-3.5 h-3.5 shrink-0" aria-hidden="true" data-id="element-509" data-mp="25h8s4wa650fj" />
              <span className="capitalize" data-id="element-510" data-mp="kxmykl0q34dr">{dateLabel}</span>
              {event.time ? ` · ${event.time}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="p-2 -mr-2 -mt-1 text-[#F5EDE3]/50 hover:text-[#F5EDE3] hover:bg-[#F5EDE3]/5 rounded-full transition-colors shrink-0" aria-label="Stäng" data-id="element-511" data-mp="pwgbocc3ktde">
            <X className="w-5 h-5" data-id="element-512" data-mp="27opc1l6v58j" />
          </button>
        </div>

        <div className="p-6" data-id="element-513" data-mp="26dl60ntzvgw">
          {!sent ? <form onSubmit={handleSubmit} noValidate className="space-y-4" data-id="element-514" data-mp="69s695qmn217">
              {event.bookingOptions && <fieldset aria-describedby={choiceTouched && choiceError ? 'su-choice-error' : undefined} data-id="element-515" data-mp="3ofwxpr327myn">
                  <legend className="block text-xs font-semibold text-[#F5EDE3]/70 mb-1.5" data-id="element-516" data-mp="1mn8sjc6uupg8">
                    {event.bookingOptions.question} *
                  </legend>
                  <div className="space-y-2" data-id="element-517" data-mp="1q5g2anhj8a2s">
                    {event.bookingOptions.choices.map((c, i) => {
                const selected = choice === c.label;
                return <label key={c.label} className={`flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors duration-150 ${selected ? 'border-[#FF7A1A] bg-[#FF7A1A]/10' : choiceTouched && choiceError ? 'border-[#F87171] bg-[#0E0C0B]' : 'border-[#F5EDE3]/15 bg-[#0E0C0B] hover:border-[#F5EDE3]/35'}`} data-id="element-518" data-mp="1g7082bn7kkp2" data-mpi={i}>
                          <input id={`su-choice-${i}`} type="radio" name="booking-choice" value={c.label} checked={selected} onChange={() => setChoice(c.label)} className="sr-only peer" data-id="element-519" data-mp="2rmf5thn8rgl8" />
                          <span className={`mt-0.5 w-5 h-5 shrink-0 rounded-md border-2 flex items-center justify-center transition-colors duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-[#FF7A1A]/50 ${selected ? 'bg-[#FF7A1A] border-[#FF7A1A]' : 'border-[#F5EDE3]/40'}`} aria-hidden="true" data-id="element-520" data-mp="345avjt6vhbnb">
                            {selected && <Check className="w-3.5 h-3.5 text-[#1A0F08]" strokeWidth={3} data-id="element-521" data-mp="3rtu489tu3qfx" />}
                          </span>
                          <span className="min-w-0" data-id="element-522" data-mp="qxu82seg8j5u">
                            <span className="block text-sm font-bold text-[#F5EDE3]" data-id="element-523" data-mp="r0ggsvd2t4pn">
                              {c.label}
                            </span>
                            {c.description && <span className="block text-xs text-[#F5EDE3]/55 mt-0.5" data-id="element-524" data-mp="zwo34ec4t1ha">
                                {c.description}
                              </span>}
                          </span>
                        </label>;
              })}
                  </div>
                  {choiceTouched && choiceError && <p id="su-choice-error" className="mt-1.5 text-xs font-medium text-[#FCA5A5]" data-id="element-525" data-mp="h295as1k3ev1">
                      {choiceError}
                    </p>}
                </fieldset>}
              {partyCfg && <div data-id="element-526" data-mp="1v85igsg2tqvi">
                  <label htmlFor="su-party" className="block text-xs font-semibold text-[#F5EDE3]/70 mb-1.5" data-id="element-527" data-mp="1v7lxt3z1kods">
                    {partyCfg.label} *
                  </label>
                  <div className="flex items-center gap-3" data-id="element-528" data-mp="1m8qvw43160ga">
                    <div className={`flex items-center h-12 rounded-lg bg-[#0E0C0B] border transition-[border-color] duration-150 ${partyTouched && partyError ? 'border-[#F87171]' : 'border-[#F5EDE3]/15 focus-within:border-[#FF7A1A]'}`} data-id="element-529" data-mp="1r60qrz96v4bi">
                      <button type="button" onClick={() => stepParty(-1)} disabled={party !== '' && partyNum <= partyCfg.min} className="w-11 h-full text-xl font-bold text-[#F5EDE3]/70 hover:text-[#FF9A4D] disabled:opacity-30 disabled:hover:text-[#F5EDE3]/70 transition-colors" aria-label="En person färre" data-id="element-530" data-mp="29x1m8db3ohqb">
                        −
                      </button>
                      <input id="su-party" type="number" inputMode="numeric" min={partyCfg.min} max={partyCfg.max} step={1} value={party} placeholder={String(partyCfg.min)} onChange={e => setParty(e.target.value)} onBlur={() => setPartyTouched(true)} aria-invalid={!!(partyTouched && partyError)} aria-describedby="su-party-hint" className="w-14 h-full bg-transparent text-center text-lg font-bold text-[#F5EDE3] placeholder:text-[#F5EDE3]/25 outline-none tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" data-id="element-531" data-mp="zqucl2o2cmgk" />
                      <button type="button" onClick={() => stepParty(1)} disabled={party !== '' && partyNum >= partyCfg.max} className="w-11 h-full text-xl font-bold text-[#F5EDE3]/70 hover:text-[#FF9A4D] disabled:opacity-30 disabled:hover:text-[#F5EDE3]/70 transition-colors" aria-label="En person till" data-id="element-532" data-mp="3jmallo1j0gr4">
                        +
                      </button>
                    </div>
                    <span className="text-sm text-[#F5EDE3]/60" data-id="element-533" data-mp="l32ee6q8z2jp">personer</span>
                  </div>
                  <p id="su-party-hint" className={`mt-1.5 text-xs font-medium ${partyTouched && partyError ? 'text-[#FCA5A5]' : 'text-[#FF9A4D]'}`} data-id="element-534" data-mp="g5kd92ih1rm9">
                    {partyTouched && partyError ? partyError : partyCfg.hint}
                  </p>
                </div>}
              {selectedChoice?.note && <p className="rounded-lg bg-[#FF7A1A]/10 border border-[#FF7A1A]/30 px-3 py-2.5 text-sm text-[#F5EDE3]/90 leading-relaxed" data-id="element-535" data-mp="3rxbqksv29bik">
                  {selectedChoice.note}
                </p>}
              {isChild && <>
                  <div data-id="element-536" data-mp="ah3wy1xx5r93">
                    <h3 className="text-sm font-bold text-[#FF9A4D]" data-id="element-537" data-mp="agkbib928kqb">
                      Barn som anmäls
                    </h3>
                    <p className="mt-0.5 text-xs text-[#F5EDE3]/60 leading-relaxed" data-id="element-538" data-mp="felw50m4ws3w">
                      Varje barn som ska vara med måste anmälas med för- och
                      efternamn. Har du flera barn lägger du till dem här.
                    </p>
                  </div>
                  <div className="space-y-3" data-id="element-539" data-mp="2la6ozkmqkbx4">
                    {children.map((c, i) => <div key={c.id} className="rounded-xl border border-[#F5EDE3]/10 bg-[#0E0C0B]/50 p-3" data-id="element-540" data-mp="2drqgibqic8ia" data-mpi={i}>
                        <div className="flex items-center justify-between mb-2 min-h-[24px]" data-id="element-541" data-mp="241dwckmluhco">
                          <span className="text-xs font-bold text-[#F5EDE3]/80" data-id="element-542" data-mp="1zzse0jraxav3">
                            Barn {i + 1}
                          </span>
                          {children.length > 1 && <button type="button" onClick={() => removeChild(c.id)} className="inline-flex items-center gap-1 px-2 py-1 -mr-1 rounded-md text-xs font-semibold text-[#F5EDE3]/55 hover:text-[#FCA5A5] hover:bg-[#F87171]/10 transition-colors" aria-label={`Ta bort barn ${i + 1}`} data-id="element-543" data-mp="2rr89l3au67xg">
                              <X className="w-3.5 h-3.5" aria-hidden="true" data-id="element-544" data-mp="17cg0t0716feg" />
                              Ta bort
                            </button>}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-545" data-mp="2m63alntvpeac">
                          {renderChildField(c, childErrors[i], 'first', 'Förnamn *')}
                          {renderChildField(c, childErrors[i], 'last', 'Efternamn *')}
                        </div>
                      </div>)}
                  </div>
                  {children.length < MAX_CHILDREN && <button type="button" onClick={addChild} className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed border-[#FF7A1A]/60 text-sm font-bold text-[#FF9A4D] hover:bg-[#FF7A1A]/10 transition-colors" data-id="element-546" data-mp="34wunv0gzlou0">
                      <Plus className="w-4 h-4" aria-hidden="true" data-id="element-547" data-mp="1yi3hwid0bdt4" />
                      Lägg till ett barn till
                    </button>}
                  <div className="pt-2 border-t border-[#F5EDE3]/10" data-id="element-548" data-mp="1lyg37euaaj9q">
                    <h3 className="pt-3 text-sm font-bold text-[#FF9A4D]" data-id="element-549" data-mp="1zxo2e7qg5rdx">
                      Förälder / kontaktperson
                    </h3>
                    <p className="mt-0.5 text-xs text-[#F5EDE3]/55" data-id="element-550" data-mp="1uzmgzg5jtfza">
                      Så att vi kan nå dig under kvällen.
                    </p>
                  </div>
                </>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-551" data-mp="12b8ikzajshn7">
                {renderField('firstName', isChild ? 'Förälderns förnamn *' : 'Förnamn *', {
              type: 'text',
              autoComplete: 'given-name',
              maxLength: 40
            })}
                {renderField('lastName', isChild ? 'Förälderns efternamn *' : 'Efternamn *', {
              type: 'text',
              autoComplete: 'family-name',
              maxLength: 40
            })}
              </div>
              {renderField('email', 'E-post *', {
            type: 'email',
            autoComplete: 'email',
            inputMode: 'email',
            placeholder: 'namn@exempel.se'
          })}
              {renderField('phone', 'Telefon *', {
            type: 'tel',
            autoComplete: 'tel',
            inputMode: 'tel',
            placeholder: '070-123 45 67'
          })}

              {event.formNote && <p className="rounded-lg bg-[#FF7A1A]/10 border border-[#FF7A1A]/30 px-3 py-2.5 text-sm text-[#F5EDE3]/90 leading-relaxed" data-id="element-552" data-mp="2emuka0jnnsj2">
                  {event.formNote}
                </p>}

              {error && <p role="alert" className="p-3 bg-[#F87171]/10 text-[#FCA5A5] text-sm rounded-lg border border-[#F87171]/30" data-id="element-553" data-mp="3pzwri4zv75qk">
                  {error}
                </p>}

              <div className="pt-2" data-id="element-554" data-mp="3qfmwex0btoj8">
                <button type="submit" disabled={isSubmitting} className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#FF7A1A] text-[#1A0F08] font-bold hover:bg-[#FF9A4D] disabled:opacity-60 disabled:cursor-wait transition-colors" data-id="element-555" data-mp="3qek4qmv6ji29">
                  {isSubmitting && <span className="w-4 h-4 rounded-full border-2 border-[#1A0F08]/30 border-t-[#1A0F08] animate-spin" aria-hidden="true" data-id="element-556" data-mp="37g8xe0nqpnlv" />}
                  {isSubmitting ? 'Skickar…' : event.ctaLabel ?? 'Boka min plats'}
                </button>
                <p className="text-center text-xs text-[#F5EDE3]/45 mt-3" data-id="element-557" data-mp="1kwsjxk26p2ft">
                  Vi bekräftar din plats via mejl.
                </p>
              </div>
            </form> : <div className="text-center py-6 space-y-4" data-id="element-558" data-mp="1b70mmi2p9v7n">
              <div className="w-14 h-14 bg-[#FF7A1A]/15 rounded-full flex items-center justify-center mx-auto" data-id="element-559" data-mp="1b6hflwel8r8e">
                <CheckCircle2 className="w-7 h-7 text-[#FF7A1A]" data-id="element-560" data-mp="1g494sinulz8w" />
              </div>
              <h3 className="text-2xl font-bold" data-id="element-561" data-mp="1179u2lxp9qa7">Tack, {firstName}!</h3>
              <p className="text-[#F5EDE3]/70 max-w-sm mx-auto text-sm leading-relaxed" data-id="element-562" data-mp="168647h8na8o2">
                {isChild ? `Vi har tagit emot anmälan för ${childFirstNamesText} till ${event.title}. Du får en bekräftelse från oss på ${values.email.trim()}.` : `Vi har tagit emot din bokning till ${event.title}. Du får en bekräftelse från oss på ${values.email.trim()}.`}
              </p>
              <div className="pt-4" data-id="element-563" data-mp="2rjxxzrrp3ui5">
                <button onClick={onClose} className="px-8 py-3 rounded-full border border-[#F5EDE3]/40 text-sm font-bold hover:bg-[#F5EDE3] hover:text-[#0E0C0B] transition-colors" data-id="element-564" data-mp="2riwinb3nclum">
                  Stäng
                </button>
              </div>
            </div>}
        </div>
      </motion.div>
    </motion.div>;
}